import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppData } from '@shared/models/appdata.model';
import { CustomerResponse } from '../models/customer-response.model';
import { InFlightOrder } from '@core/models/in-flight-order.model';
import { CustomerServiceOffering, CustomerServiceOfferingResponse } from '../models/customer-service-offering.model';
import { CustomerSearchResponse } from '@shared/models/customer-search-response.model';
import { Subscription } from '@shared/models/subscription-model';
import { CustomerEquipmentUpgradeAgreement } from '../../mobilephone/models/equipment-upgrade-agreement.model';
import { CustomerEquipment } from '../models/customer-equipment';
import { AssignedCustomerServiceModel } from '../models/assigned-customer-services.model';
import { CanHaveTrialResponse } from '../models/can-have-trial-response.model';

interface GetExistingCustomerRequest {
  ssn: string;
  firstName: string;
  lastName: string;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private http = inject(HttpClient);
  private appData = inject(AppData);

  apiUrl = this.appData.apiUrl + '/api/v1';

  get(id: string): Observable<CustomerResponse> {
    const url = `${this.apiUrl}/customer/${id}`;
    return this.http.get<CustomerResponse>(url);
  }

  getSubscriptions(customerId: string): Observable<Subscription[]> {
    const customerServicesApiUrl = `${this.apiUrl}/customer/${customerId}/subscriptions`;
    return this.http.get<Subscription[]>(customerServicesApiUrl);
  }

  getExistingCustomerId(existingCustomer: GetExistingCustomerRequest): Observable<number> {
    const url = `${this.apiUrl}/customer/existing-customer`;
    return this.http.post<number>(url, existingCustomer);
  }

  getCanHaveTrial(customerId: string, msisdn: string): Observable<CanHaveTrialResponse> {
    const url = `${this.apiUrl}/customer/${customerId}/can-have-trial`;
    return this.http.get<CanHaveTrialResponse>(url, { headers: { msisdn: msisdn } });
  }

  getInFlightOrdersForCustomer(customerId: string): Observable<InFlightOrder[]> {
    const url = `${this.apiUrl}/customer/${customerId}/orders`;
    return this.http.get<InFlightOrder[]>(url);
  }

  getCustomerServiceOfferings(): Observable<CustomerServiceOfferingResponse> {
    const url = `${this.apiUrl}/customer/customer-service-offerings`;
    return this.http.get<CustomerServiceOfferingResponse>(url);
  }

  getCustomerServiceOffering(offeringId: string): Observable<CustomerServiceOffering> {
    const url = `${this.apiUrl}/customer/customer-service-offerings/${offeringId}`;
    return this.http.get<CustomerServiceOffering>(url);
  }

  getAvailableCustomerServiceOfferings(customerId: string): Observable<CustomerServiceOfferingResponse> {
    const url = `${this.apiUrl}/customer/${customerId}/customer-service-offerings`;
    return this.http.get<CustomerServiceOfferingResponse>(url);
  }

  getAssignedCustomerServices(customerId: string): Observable<AssignedCustomerServiceModel[]> {
    const url = `${this.apiUrl}/customer/${customerId}/assigned-customer-services`;
    return this.http.get<AssignedCustomerServiceModel[]>(url);
  }

  customerSearch(query: string): Observable<CustomerSearchResponse> {
    const url = `${this.apiUrl}/customer/search?query=${query}`;
    return this.http.get<CustomerSearchResponse>(url);
  }

  getEquipment(customerId: string): Observable<CustomerEquipment[]> {
    const url = `${this.appData.apiUrl}/api/v1/customer/${customerId}/equipment`;
    return this.http.get<CustomerEquipment[]>(url);
  }

  getMobilBytteAgreements(customerId: string): Observable<CustomerEquipmentUpgradeAgreement[]> {
    const url = `${this.appData.apiUrl}/api/v1/customer/${customerId}/mobilbytteagreements`;
    return this.http.get<CustomerEquipmentUpgradeAgreement[]>(url);
  }
}
